import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Loader2, DollarSign, CheckCircle, Clock } from "lucide-react";
import { useForm } from "react-hook-form";

interface FinancialAidFormData {
  application_type: string;
  annual_income?: string;
  household_size?: string;
  dependency_status?: string;
  additional_notes?: string;
}

const FinancialAid = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;
      
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', session.user.id)
        .single();
      
      return data;
    },
  });

  const { data: application } = useQuery({
    queryKey: ['application', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return null;
      
      const { data } = await supabase
        .from('applications')
        .select('*')
        .eq('profile_id', profile.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();
      
      return data;
    },
    enabled: !!profile?.id,
  });

  const { data: enrollment } = useQuery({
    queryKey: ['enrollment', application?.id],
    queryFn: async () => {
      if (!application?.id) return null;
      
      const { data } = await supabase
        .from('enrollments')
        .select('*')
        .eq('application_id', application.id)
        .single();
      
      return data;
    },
    enabled: !!application?.id,
  });

  const { data: financialAid, isLoading } = useQuery({
    queryKey: ['financial-aid', enrollment?.id],
    queryFn: async () => {
      if (!enrollment?.id) return null;
      
      const { data } = await supabase
        .from('financial_aid_applications')
        .select('*')
        .eq('enrollment_id', enrollment.id)
        .single();
      
      return data;
    },
    enabled: !!enrollment?.id,
  });

  const { register, handleSubmit, watch, setValue } = useForm<FinancialAidFormData>({
    values: financialAid ? {
      application_type: financialAid.application_type,
      annual_income: financialAid.annual_income?.toString(),
      household_size: financialAid.household_size?.toString(),
      dependency_status: financialAid.dependency_status || '',
      additional_notes: financialAid.additional_notes || '',
    } : undefined,
  });

  const saveMutation = useMutation({
    mutationFn: async (data: FinancialAidFormData) => {
      if (!enrollment?.id) throw new Error('No enrollment found');

      const aidData = {
        application_type: data.application_type,
        annual_income: data.annual_income ? parseFloat(data.annual_income) : null,
        household_size: data.household_size ? parseInt(data.household_size) : null,
        dependency_status: data.dependency_status,
        additional_notes: data.additional_notes,
      };

      if (financialAid) {
        const { error } = await supabase
          .from('financial_aid_applications')
          .update(aidData)
          .eq('enrollment_id', enrollment.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('financial_aid_applications')
          .insert({
            ...aidData,
            enrollment_id: enrollment.id,
            aid_status: 'not_submitted',
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-aid'] });
      toast({
        title: "Success",
        description: "Financial aid information saved",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const submitMutation = useMutation({
    mutationFn: async (data: FinancialAidFormData) => {
      if (!enrollment?.id) throw new Error('No enrollment found');

      const aidData = {
        application_type: data.application_type,
        annual_income: data.annual_income ? parseFloat(data.annual_income) : null,
        household_size: data.household_size ? parseInt(data.household_size) : null,
        dependency_status: data.dependency_status,
        additional_notes: data.additional_notes,
        aid_status: 'submitted',
      };

      if (financialAid) {
        const { error } = await supabase
          .from('financial_aid_applications')
          .update(aidData)
          .eq('enrollment_id', enrollment.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('financial_aid_applications')
          .insert({
            ...aidData,
            enrollment_id: enrollment.id,
          });
        if (error) throw error;
      }
    },
    onSuccess: async (_, data) => {
      queryClient.invalidateQueries({ queryKey: ['financial-aid'] });
      
      // Log activity
      await supabase.functions.invoke("log-activity", {
        body: {
          event_type: "financial_aid.submitted",
          metadata: {
            enrollment_id: enrollment?.id,
            application_type: data.application_type,
            annual_income: data.annual_income,
          },
        },
      });
      
      toast({
        title: "Success",
        description: "Financial aid application submitted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSave = (data: FinancialAidFormData) => {
    saveMutation.mutate(data);
  };

  const onSubmit = (data: FinancialAidFormData) => {
    submitMutation.mutate(data);
  };

  // Add realtime subscription for financial aid
  useEffect(() => {
    if (!enrollment?.id) return;

    const channel = supabase
      .channel('financial-aid-updates')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'financial_aid_applications', filter: `enrollment_id=eq.${enrollment.id}` },
        () => queryClient.invalidateQueries({ queryKey: ['financial-aid'] })
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [enrollment?.id, queryClient]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const statusColors = {
    not_submitted: 'bg-gray-500',
    submitted: 'bg-blue-500',
    under_review: 'bg-yellow-500',
    approved: 'bg-green-500',
    denied: 'bg-red-500',
  };

  return (
    <div className="p-8 space-y-6 max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold">Financial Aid</h1>
        <p className="text-muted-foreground mt-1">Apply for financial assistance to support your education</p>
      </div>

      {financialAid && financialAid.aid_status !== 'not_submitted' && (
        <Card>
          <CardHeader>
            <CardTitle>Application Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Status:</span>
              <Badge className={statusColors[financialAid.aid_status as keyof typeof statusColors]}>
                {financialAid.aid_status.replace('_', ' ').toUpperCase()}
              </Badge>
            </div>
            {financialAid.aid_amount_offered && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Aid Offered:</span>
                <span className="font-semibold text-lg">
                  ${financialAid.aid_amount_offered.toLocaleString()}
                </span>
              </div>
            )}
            {financialAid.efc_amount && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Expected Family Contribution:</span>
                <span className="font-medium">
                  ${financialAid.efc_amount.toLocaleString()}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Financial Aid Application
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="application_type">Aid Type *</Label>
              <Select
                value={watch('application_type')}
                onValueChange={(value) => setValue('application_type', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select aid type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="institutional">Institutional Scholarship</SelectItem>
                  <SelectItem value="scholarship">Merit Scholarship</SelectItem>
                  <SelectItem value="payment_plan">Payment Plan</SelectItem>
                  <SelectItem value="work_study">Campus Work-Study</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="annual_income">Annual Family Income</Label>
              <Input
                id="annual_income"
                type="number"
                {...register('annual_income')}
                placeholder="50000"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="household_size">Household Size</Label>
              <Input
                id="household_size"
                type="number"
                {...register('household_size')}
                placeholder="4"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dependency_status">Dependency Status</Label>
              <Select
                value={watch('dependency_status')}
                onValueChange={(value) => setValue('dependency_status', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select dependency status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dependent">Dependent</SelectItem>
                  <SelectItem value="independent">Independent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="additional_notes">Additional Information</Label>
              <Textarea
                id="additional_notes"
                {...register('additional_notes')}
                placeholder="Describe any special circumstances or additional information..."
                rows={4}
              />
            </div>

            <div className="p-4 bg-muted rounded-lg">
              <h4 className="font-medium mb-2">Important Financial Aid Information</h4>
              <p className="text-sm text-muted-foreground mb-2">
                HTICU offers institutional scholarships, merit-based awards, and flexible payment plans to help make your education affordable. As a faith-based institution operating under Florida's religious exemption, HTICU does not participate in federal Title IV financial aid programs (FAFSA).
              </p>
              <p className="text-sm text-muted-foreground">
                For questions about financial assistance options, please contact our admissions office at{" "}
                <a href="mailto:admin@hticu.org" className="text-primary hover:underline">
                  admin@hticu.org
                </a>
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleSubmit(onSave)}
                disabled={saveMutation.isPending || (financialAid?.aid_status !== 'not_submitted' && financialAid?.aid_status !== undefined)}
              >
                {saveMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Draft
              </Button>
              <Button
                type="button"
                onClick={handleSubmit(onSubmit)}
                disabled={submitMutation.isPending}
              >
                {submitMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {financialAid?.aid_status === 'not_submitted' || !financialAid ? 'Submit Application' : 'Update Application'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default FinancialAid;
